import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../../components/ui/select";
import { Send, Plus, Trash2, Bot, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function KipAssistant() {
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("claude-sonnet-4-5-20250929");
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/kip/chat/models").then(({ data }) => setModels(data));
    loadSessions();
  }, []);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const loadSessions = async () => {
    const { data } = await api.get("/kip/chat/sessions");
    setSessions(data);
  };
  const openSession = async (sid) => {
    const { data } = await api.get(`/kip/chat/sessions/${sid}`);
    setActiveSession(sid);
    setMessages(data.messages || []);
    if (data.model) setModel(data.model);
  };
  const newSession = () => { setActiveSession(null); setMessages([]); setInput(""); };
  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this conversation?")) return;
    await api.delete(`/kip/chat/sessions/${sid}`);
    if (activeSession === sid) newSession();
    loadSessions();
  };

  const send = async () => {
    if (!input.trim()) return;
    const userMsg = { role: "user", text: input };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/kip/chat", { session_id: activeSession, message: userMsg.text, model });
      setActiveSession(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.response, model: data.model }]);
      loadSessions();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="kip-assistant-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Knowledge Intelligence</div>
          <h1 className="font-sans text-3xl">AI Assistant</h1>
          <p className="text-sm text-slate-600 mt-1">Ask Indian-tax, audit, valuation, IND AS, GST and company-law questions.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="p-3 lg:col-span-1 flex flex-col" style={{ height: "70vh" }}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-slate-500">Sessions</div>
            <Button size="sm" variant="ghost" onClick={newSession} data-testid="new-chat-btn"><Plus className="h-3 w-3"/></Button>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1">
            {sessions.map((s) => (
              <div key={s.id} className={`group flex items-center gap-1 text-sm rounded p-2 cursor-pointer hover:bg-slate-100 ${activeSession === s.id ? "bg-slate-100" : ""}`} onClick={() => openSession(s.id)} data-testid={`session-${s.id}`}>
                <MessageSquare className="h-3 w-3 text-slate-500 shrink-0"/>
                <div className="flex-1 truncate text-slate-700">{s.title || "Untitled"}</div>
                <button onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600"><Trash2 className="h-3 w-3"/></button>
              </div>
            ))}
            {!sessions.length && <div className="text-xs text-slate-400 text-center p-4">No conversations yet</div>}
          </div>
        </Card>

        <Card className="p-0 lg:col-span-3 flex flex-col" style={{ height: "70vh" }}>
          <div className="border-b border-slate-200 p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Bot className="h-4 w-4"/>
              <span>Model:</span>
            </div>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger className="w-56" data-testid="model-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {models.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}{m.default && " (default)"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {!messages.length && (
              <div className="text-center text-slate-400 p-8">
                <Bot className="h-10 w-10 mx-auto mb-2"/>
                <p>Start a conversation about Indian tax, audit or accounting.</p>
                <div className="text-xs mt-2 text-slate-500 space-y-1">
                  <p>Try: "Summarise key changes in CARO 2020 vs CARO 2016"</p>
                  <p>Try: "Explain IND AS 115's 5-step revenue model with an example"</p>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-3xl rounded-lg p-3 text-sm ${m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-900"}`} data-testid={`msg-${m.role}-${i}`}>
                  <pre className="whitespace-pre-wrap font-sans">{m.text}</pre>
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-slate-100 rounded-lg p-3 text-sm text-slate-500 animate-pulse">Thinking…</div></div>}
            <div ref={endRef}/>
          </div>

          <div className="border-t border-slate-200 p-3 flex gap-2">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type your question…" className="resize-none" rows={2} disabled={busy} data-testid="chat-input"/>
            <Button onClick={send} disabled={busy || !input.trim()} data-testid="chat-send"><Send className="h-4 w-4"/></Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
